import { computed, Injectable, Signal, signal, untracked } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import {
  PackingList,
  PackingListAction,
  PackingListAuthData,
  PackingListOrder,
  PackingListOrderData,
  PackingListPackData,
  PackingListPackages,
  PackingListPickData,
  PackingListStationData,
} from '../models/packing-list.entity'
import { PackingListsRepository } from '../packing-lists.repository'
import { PickingListsService, PickingListType } from '../../picking-lists'
import { Order, OrdersService, addOrderPackage } from '../../orders'
import {
  getPackingNextOrder,
  isPackingOrderLast,
  isPackingOrderPackable,
  isPackingOrderPacked,
  isPackingOrderWaiting,
} from '../libs/packing-orders.lib'
import { getPackingActionWeightToPack } from '../libs/packing-products.lib'
import { PackingListPackingStep } from '../models/packing-list.types'
import { Channel, ChannelsService } from '../../channels'
import { Carrier, CarriersService } from '../../carriers'
import { Payment, PaymentsService } from '../../payments'
import { Tag, TagsService } from '../../tags'
import { defaultIfEmpty, firstValueFrom } from 'rxjs'
import { ProductsService } from '../../products'
import { uniq } from 'lodash'
import {
  TrackedBarcodeMode,
  TrackedBarcodesRepository,
} from '../../tracked-barcodes'

interface PackingListDetailState {
  step: PackingListPackingStep
  packingData?: PackingListPickData
  stationData?: PackingListStationData
  orderData?: PackingListOrderData
  lastOrder?: Order
  lastPackingList?: PackingList
  error?: HttpErrorResponse
  carrier?: Carrier
  channel?: Channel
  payment?: Payment
  tags?: Tag[]
  isLoading: boolean
  isPrinting: boolean
  orderPacked: boolean
  beginPack: boolean
}

const PACKING_INITIAL_STATE: PackingListDetailState = {
  step: 'detail',
  isLoading: false,
  isPrinting: false,
  orderPacked: false,
  beginPack: false,
}

@Injectable()
export class PackingListDetailStore {
  constructor(
    private packingListsRepository: PackingListsRepository,
    private pickingListsService: PickingListsService,
    private ordersService: OrdersService,
    private carriersService: CarriersService,
    private channelsService: ChannelsService,
    private paymentsService: PaymentsService,
    private tagsService: TagsService,
    private productsService: ProductsService,
    private trackedBarcodeRepository: TrackedBarcodesRepository,
  ) {}

  public state = signal<PackingListDetailState>(PACKING_INITIAL_STATE)

  // Selectors

  readonly isLoading = computed(() => this.state().isLoading)
  readonly isErrored = computed(() => !!this.state().error)
  readonly isPrinting = computed(() => this.state().isPrinting)
  readonly orderPacked = computed(() => this.state().orderPacked)
  readonly step = computed(() => this.state().step)
  readonly packingData = computed(() => this.state().packingData)
  readonly pickingList = computed(() => this.packingData()?.pickingList)
  readonly packingList = computed(() => this.packingData()?.packingList)
  readonly orderData = computed(() => this.state()?.orderData)
  readonly order = computed(() => this.orderData()?.order)
  readonly action = computed(() => this.orderData()?.action)
  readonly products = computed(() => this.orderData()?.products)
  readonly packages = computed(() => this.orderData()?.packages)
  readonly carrier = computed(() => this.state()?.carrier)
  readonly channel = computed(() => this.state()?.channel)
  readonly payment = computed(() => this.state()?.payment)
  readonly tags = computed(() => this.state()?.tags)
  readonly additionalPackages = computed(
    () => this.orderData()?.additionalPackages,
  )
  readonly stationData = computed(() => this.state()?.stationData)
  readonly warehouseId = computed(() => this.stationData()?.warehouseId)
  readonly lastOrder = computed(() => this.state()?.lastOrder)
  readonly lastPackingList = computed(() => this.state()?.lastPackingList)
  readonly beginPack = computed(() => this.state()?.beginPack)
  readonly stepToFocus: Signal<PackingListPackingStep> = computed(() => {
    const currentState = this.state()

    if (
      currentState.isLoading ||
      currentState.isPrinting ||
      currentState.orderPacked ||
      currentState.step === 'detail'
    ) {
      return untracked(this.stepToFocus)
    }

    return currentState.step || 'detail'
  })
  readonly orderIsLast: Signal<boolean> = computed(() => {
    const packingList = this.packingList()
    const order = this.order()
    if (!packingList || !order) {
      return false
    }
    return isPackingOrderLast(packingList, order._id)
  })
  readonly orderIsWaiting: Signal<boolean> = computed(() => {
    const packingList = this.packingList()
    const order = this.order()
    if (!packingList || !order) {
      return false
    }
    return isPackingOrderWaiting(packingList, order._id)
  })
  readonly orderIsPacked: Signal<boolean> = computed(() => {
    const packingList = this.packingList()
    const order = this.order()
    if (!packingList || !order) {
      return false
    }
    const isPacked = isPackingOrderPacked(packingList, order._id)
    return isPacked
  })
  readonly orderIsPackable: Signal<boolean> = computed(() => {
    const packingList = this.packingList()
    const order = this.order()
    if (!packingList || !order) {
      return false
    }
    return isPackingOrderPackable(packingList, order._id)
  })

  // Effects

  async load(packingListId: string): Promise<void> {
    this.unsetError()
    const pickData = await firstValueFrom(
      this.packingListsRepository.loadPackingDataById$(packingListId),
    )
    const packingList = pickData.packingList
    this.setPackingPickData(
      pickData,
      !!(packingList && pickData.orders?.length),
    )
    if (packingList && pickData.orders?.length) {
      const packingPackData = await firstValueFrom(
        this.packingListsRepository
          .loadPackingPackData$(packingList, pickData.orders)
          .pipe(defaultIfEmpty(undefined)),
      )
      if (packingPackData) {
        const order = packingPackData.order
        const [payment, channel, carrier, tags] = await Promise.all([
          this.getPayment(order?.header.paymentType),
          this.getChannel(order?.header.channel),
          this.getCarrier(order?.carrierId),
          this.getTags(order),
        ])

        this.setPackingPackData({
          packingPackData,
          step: 'detail',
          payment,
          channel,
          carrier,
          tags,
        })
      }
    }
  }

  async loadByPickingListId(pickingListId: string): Promise<void> {
    this.unsetError()
    const pickingList = await firstValueFrom(
      this.pickingListsService
        .read$(pickingListId)
        .pipe(defaultIfEmpty(undefined)),
    )
    if (!pickingList) {
      this.setIsLoading(false)
      this.packingListsRepository.notify({ code: 'PICKING_LIST_NOT_FOUND' })
      return
    }

    const pickData = await firstValueFrom(
      this.packingListsRepository
        .loadPackingPickDataByPickingId$(pickingListId)
        .pipe(defaultIfEmpty(undefined)),
    )
    if (!pickData) {
      this.setIsLoading(false)
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }
    this.setPackingPickData(pickData, false)
  }

  async startPacking(): Promise<void> {
    const packingData = this.packingData()
    const printerId = this.stationData()?.printerId
    if (!printerId || !packingData) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }

    const { pickingList, orders } = packingData
    const { A4PrinterId, packagingLocation } = this.stationData() ?? {}

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .startPacking$(
          pickingList,
          orders,
          printerId,
          A4PrinterId,
          packagingLocation?._id,
        )
        .subscribe({
          next: async (data) => {
            if (!data) {
              this.setIsLoading(false)
              resolve()
              return
            }
            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data.order?.header.paymentType),
              this.getChannel(data.order?.header.channel),
              this.getCarrier(data.order?.carrierId),
              this.getTags(data.order),
            ])
            this.setPackingPackData({
              packingPackData: data,
              payment,
              channel,
              carrier,
              tags,
            })
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async restorePacking(): Promise<void> {
    const packingData = this.packingData()
    const stationData = this.stationData()

    if (
      !packingData?.packingList ||
      !stationData?.currentUserId ||
      !stationData?.printerId
    ) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }

    const { packingList, pickingList, orders } = packingData
    const { currentUserId, printerId, A4PrinterId, packagingLocation } =
      stationData

    this.unsetError()

    const restoredPackingData =
      await this.packingListsRepository.restorePacking$(
        packingList,
        pickingList,
        orders,
        currentUserId,
        printerId,
        A4PrinterId,
        packagingLocation?._id,
      )
    const [payment, channel, carrier, tags] = await Promise.all([
      this.getPayment(restoredPackingData?.order?.header.paymentType),
      this.getChannel(restoredPackingData?.order?.header.channel),
      this.getCarrier(restoredPackingData?.order?.carrierId),
      this.getTags(restoredPackingData?.order),
    ])

    if (restoredPackingData) {
      this.setPackingPackData({
        packingPackData: restoredPackingData,
        payment,
        channel,
        carrier,
        tags,
      })
    }
  }

  async takeUpPacking(): Promise<void> {
    const packingData = this.packingData()
    const stationData = this.stationData()

    if (!packingData?.packingList || !stationData?.printerId) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }
    const { packingList, orders } = packingData
    const { printerId, A4PrinterId, packagingLocation } = stationData

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .takeUpPacking$(
          packingList._id,
          orders,
          printerId,
          A4PrinterId,
          packagingLocation?._id,
        )
        .subscribe({
          next: async (data) => {
            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data?.order?.header.paymentType),
              this.getChannel(data?.order?.header.channel),
              this.getCarrier(data?.order?.carrierId),
              this.getTags(data?.order),
            ])

            this.setPackingPackData({
              packingPackData: data,
              payment,
              channel,
              carrier,
              tags,
            })
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async skipOrder(): Promise<void> {
    const packingData = this.packingData()

    if (!packingData?.packingList || !packingData.orders) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }

    const orderData = this.orderData()

    const packingList = packingData.packingList
    const currentOrder = orderData?.order
    const nextOrderPack = getPackingNextOrder(packingList, currentOrder?._id)
    const nextOrder = packingData.orders.find(
      (o) => o._id === nextOrderPack._id,
    )

    if (!nextOrder) {
      this.packingListsRepository.notify({ code: 'ORDER_NOT_FOUND' })
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .loadOrderData$(packingList, nextOrder)
        .subscribe({
          next: async (data) => {
            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data?.order?.header.paymentType),
              this.getChannel(data?.order?.header.channel),
              this.getCarrier(data?.order?.carrierId),
              this.getTags(data?.order),
            ])
            this.setPackingOrderData(data, payment, channel, carrier, tags)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async scan(
    code: string,
    mode: TrackedBarcodeMode = 'KEYBOARD',
  ): Promise<void> {
    const packingData = this.packingData()
    const orderData = this.orderData()
    if (!packingData?.packingList || !packingData.orders || !orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    if (
      packingData.pickingList.type === PickingListType.pack &&
      !orderData.order
    ) {
      const scannedTote = packingData.pickingList.totes.find(
        (c) => c.code === code,
      )
      const orderToLoad = packingData.orders.find(
        (o) => o._id === scannedTote?.orderIds[0],
      )
      if (!orderData.order && packingData.packingList && orderToLoad) {
        this.unsetError()

        return new Promise((resolve, reject) => {
          this.packingListsRepository
            .loadOrderData$(packingData.packingList!, orderToLoad)
            .subscribe({
              next: async (data) => {
                const [payment, channel, carrier, tags] = await Promise.all([
                  this.getPayment(data?.order?.header.paymentType),
                  this.getChannel(data?.order?.header.channel),
                  this.getCarrier(data?.order?.carrierId),
                  this.getTags(data?.order),
                ])
                this.setPackingOrderData(data, payment, channel, carrier, tags)
                resolve()
              },
              error: (error) => {
                this.setError(error)
                reject(error)
              },
            })
        })
      }
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .scanProduct$(code, packingData, orderData)
        .subscribe({
          next: async (data) => {
            if (!data) {
              this.setIsLoading(false)
              resolve()
              return
            }

            this.trackEvents(data, code, mode)

            const [payment, channel, carrier, tags] =
              await this.loadOrderLookups(data.order)
            this.setPackingPackData({
              packingPackData: data,
              checkPack: true,
              payment,
              channel,
              carrier,
              tags,
            })
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  private async loadOrderLookups(order?: Order) {
    const results = await Promise.allSettled([
      this.getPayment(order?.header.paymentType),
      this.getChannel(order?.header.channel),
      this.getCarrier(order?.carrierId),
      this.getTags(order),
    ])
    return results.map((r) =>
      r.status === 'fulfilled' ? r.value : undefined,
    ) as [
      Payment | undefined,
      Channel | undefined,
      Carrier | undefined,
      Tag[] | undefined,
    ]
  }

  private async trackEvents(
    data: PackingListPackData,
    barcode: string | undefined,
    mode: TrackedBarcodeMode,
  ): Promise<void> {
    const locationId = this.stationData()?.packagingLocation?._id
    const createdBy = this.stationData()?.currentUserId
    const warehouseId = this.warehouseId()

    if (!locationId || !createdBy || !warehouseId) {
      return
    }

    for (const e of data.scanEvents ?? []) {
      if (!e.orderId || !e.tenantId) {
        continue
      }

      await this.trackedBarcodeRepository.trackBarcode({
        operation: 'PACKING_LIST',
        product_id: e.productId,
        barcode,
        qty: e.qty,
        location_id: locationId,
        created_by: createdBy,
        tenant_id: e.tenantId,
        warehouse_id: warehouseId,
        packing_list_id: data.packingList._id,
        order_ids: [e.orderId],
        mode,
        phase: e.phase,
        lot: e.lot,
        serial: e.serial,
        expiration_date: e.expirationDate,
      })
    }
  }

  async packAction(): Promise<void> {
    const packingData = this.packingData()
    const orderData = this.orderData()

    if (!packingData || !orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    const { packingList, orders } = packingData
    const { action, products } = orderData

    if (!packingList || !orders || !action || !products) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }

    const product = products.find((p) => p._id === action.productId)
    const qty = action.qtyToPack - action.qtyPacked

    if (!product || !qty) {
      this.packingListsRepository.notify({ code: 'PRODUCT_NOT_FOUND' })
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .packProduct$(product, packingList, orders, orderData, product.SKU, qty)
        .subscribe({
          next: async (data) => {
            if (!data) {
              this.setIsLoading(false)
              resolve()
              return
            }

            this.trackEvents(data, undefined, 'ITEM_SELECTION')

            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data?.order?.header.paymentType),
              this.getChannel(data?.order?.header.channel),
              this.getCarrier(data?.order?.carrierId),
              this.getTags(data?.order),
            ])
            this.setPackingPackData({
              packingPackData: data,
              checkPack: true,
              payment,
              channel,
              carrier,
              tags,
            })
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async packOrder(): Promise<void> {
    const packingData = this.packingData()
    const orderData = this.orderData()

    if (!packingData?.packingList || !orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .packTote$(packingData.packingList!, orderData)
        .subscribe({
          next: async (data) => {
            if (!data) {
              this.setIsLoading(false)
              resolve()
              return
            }
            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data?.order?.header.paymentType),
              this.getChannel(data?.order?.header.channel),
              this.getCarrier(data?.order?.carrierId),
              this.getTags(data?.order),
            ])
            this.setPackingPackData({
              packingPackData: data,
              checkPack: true,
              payment,
              channel,
              carrier,
              tags,
            })
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async setOrderUpdated(order: Order): Promise<void> {
    const packingData = this.packingData()

    if (!packingData?.packingList) {
      this.packingListsRepository.notify({ code: 'MISSING_PACKING_DATA' })
      return
    }

    const preservedPackages = this.orderData()?.packages
    const preservedAdditionalPackages = this.orderData()?.additionalPackages

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository
        .loadOrderData$(packingData.packingList!, order)
        .subscribe({
          next: async (data) => {
            const [payment, channel, carrier, tags] = await Promise.all([
              this.getPayment(data?.order?.header.paymentType),
              this.getChannel(data?.order?.header.channel),
              this.getCarrier(data?.order?.carrierId),
              this.getTags(data?.order),
            ])

            this.setPackingOrderData(
              {
                ...data,
                packages: preservedPackages?.length
                  ? preservedPackages
                  : data.packages,
                additionalPackages: preservedAdditionalPackages?.length
                  ? preservedAdditionalPackages
                  : data.additionalPackages,
              },
              payment,
              channel,
              carrier,
              tags,
            )
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async downloadAttachment(id: string, entityId?: string) {
    if (!entityId) {
      return
    }

    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.ordersService.downloadAttachment$(entityId, id).subscribe({
        next: (response) => {
          resolve(response)
          const contentDisposition =
            response.headers.get('Content-Disposition') ||
            'attachment; filename="download"'
          const filename =
            contentDisposition.split('filename=')[1] || 'download'
          const blob = response.body

          if (blob) {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename.replace(/"/g, '')
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            a.remove()
          }
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async getCarrier(carrierId?: string): Promise<Carrier | undefined> {
    if (!carrierId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.carriersService.read$(carrierId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getChannel(channelId?: string): Promise<Channel | undefined> {
    if (!channelId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.channelsService.read$(channelId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getPayment(paymentId?: string): Promise<Payment | undefined> {
    if (!paymentId) {
      return undefined
    }
    return new Promise((resolve) => {
      this.paymentsService.read$(paymentId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async getTags(order?: Order): Promise<Tag[] | undefined> {
    if (!order) {
      return undefined
    }

    let tagValues = order.tags ?? []
    const productIds = order.rows.map((r) => r.product._id)

    const products = productIds.length
      ? await firstValueFrom(
          this.productsService
            .list$({ _id: productIds })
            .pipe(defaultIfEmpty(undefined)),
        )
      : undefined

    if (products && products?.length > 0) {
      products.map((p) =>
        p.tags && p.tags.length > 0
          ? (tagValues = tagValues.concat(p.tags))
          : undefined,
      )
    }

    tagValues = uniq(tagValues)

    if (!tagValues || tagValues.length < 1) {
      return undefined
    }
    return new Promise((resolve) => {
      this.tagsService.list$({ value: tagValues }).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async loadOrders(
    orders: PackingListOrder[] | undefined,
  ): Promise<Order[] | undefined> {
    if (!orders || orders.length < 1) {
      return undefined
    }
    const orderIds = orders.map((o) => o._id)

    return new Promise((resolve) => {
      this.ordersService.list$({ _id: orderIds }).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async initStationData(authData: PackingListAuthData): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.packingListsRepository.loadPackingStationData$(authData).subscribe({
        next: async (data) => {
          resolve()
          this.setStationData(data)
        },
        error: (error) => {
          this.setError(error)
          reject(error)
        },
      })
    })
  }

  // Utilities
  public parseStatePackages(
    state?: PackingListDetailState,
  ): PackingListPackages | undefined {
    if (!state?.packingData?.packingList || !state.orderData?.order) {
      return undefined
    }

    // Check packages
    let packages = state.orderData.packages || []
    const additionalPackages = state.orderData.additionalPackages
    if (!state.stationData?.packagingManagement && !packages?.length) {
      packages = addOrderPackage(packages, {
        weight: getPackingActionWeightToPack(
          state.packingData.packingList,
          state.orderData.order._id,
          state.orderData.packages || [],
        ),
      })
    }

    return { packages, additionalPackages }
  }

  setOrder(order: Order) {
    const packingData = this.packingData()
    const orderData = this.orderData()
    if (!packingData || !orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    this.state.set({
      ...this.state(),
      packingData: {
        ...packingData,
        orders: packingData.orders.map((o) =>
          o._id === order._id ? order : o,
        ),
      },
      orderData: {
        ...orderData,
        order,
      },
    })
  }

  unsetLastPackingList() {
    this.state.set({
      ...this.state(),
      lastPackingList: undefined,
    })
  }

  setPackages(packagesData: PackingListPackages) {
    const orderData = this.orderData()
    if (!orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    this.state.set({
      ...this.state(),
      orderData: {
        ...orderData,
        packages: packagesData.packages,
        additionalPackages: packagesData.additionalPackages,
      },
    })
  }

  setPackingOrderAction(action: PackingListAction) {
    const orderData = this.orderData()
    if (!orderData) {
      this.packingListsRepository.notify({ code: 'MISSING_ORDER_DATA' })
      return
    }

    this.state.set({
      ...this.state(),
      orderData: {
        ...orderData,
        action,
      },
    })
  }

  setStationData(stationData: PackingListStationData) {
    this.state.set({
      ...this.state(),
      stationData,
      isLoading: false,
    })
  }

  setPackingOrderData(
    orderData: PackingListOrderData,
    payment?: Payment,
    channel?: Channel,
    carrier?: Carrier,
    tags?: Tag[],
  ) {
    this.state.set({
      ...this.state(),
      orderData,
      payment,
      channel,
      carrier,
      tags,
      isLoading: false,
    })
  }

  setPackingPackData(data: {
    packingPackData: PackingListPackData
    checkPack?: boolean
    step?: PackingListPackingStep
    payment?: Payment
    channel?: Channel
    carrier?: Carrier
    tags?: Tag[]
  }) {
    const { packingList, lastOrder, ...orderData } = data.packingPackData
    const packingData = this.packingData()
    if (
      data.checkPack === true &&
      orderData?.order &&
      isPackingOrderPacked(packingList, orderData.order._id)
    ) {
      this.packingListsRepository.notify({ code: 'ORDER_PACKED' })
    }

    this.state.set({
      ...this.state(),
      packingData: packingData
        ? {
            ...packingData,
            packingList:
              packingList.status === 'PRINTED' ? undefined : packingList,
          }
        : packingData,
      orderData: { ...orderData },
      lastOrder,
      lastPackingList:
        packingList.status === 'PRINTED' ? packingList : undefined,
      step:
        data.step ?? (packingList.status === 'PRINTED' ? 'detail' : 'packing'),
      tags: data.tags,
      payment: data.payment,
      carrier: data.carrier,
      channel: data.channel,
      isLoading: false,
      isPrinting: false,
    })
  }

  setPackingPickData(data: PackingListPickData, isLoading?: boolean) {
    this.state.set({
      ...this.state(),
      step: 'detail',
      packingData: data,
      isLoading: isLoading ?? false,
    })
  }

  setIsPrinting() {
    this.state.set({
      ...this.state(),
      isPrinting: true,
      beginPack: false,
      error: undefined,
    })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  setIsLoading(value: boolean, isPrinting?: boolean) {
    this.state.set({
      ...this.state(),
      isLoading: value,
      isPrinting: isPrinting ?? this.isPrinting(),
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  setBeingPack(value: boolean) {
    this.state.set({ ...this.state(), beginPack: value })
  }
}
